import type { ContractFilePolicy, ContractTemplateView, ContractWithVersion } from "@closer/shared";
import { CONTRACT_FILE_REJECTION_NAMES, precheckContractFile } from "@closer/shared";
import { Button, EmptyState, FileInput, FormField, InfoBanner, Input, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import {
  ChevronRightIcon,
  ContractIcon,
  SparklesIcon,
  TemplateIcon,
  UploadIcon,
} from "../components/icons";
import { StateView } from "../components/StateView";
import { api, ApiRequestError } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { uploadFile } from "../lib/fileUpload";
import { tenantKey } from "../lib/tenantQuery";
import { resolveErrorState } from "../lib/uiState";

/**
 * YENİ SÖZLEŞME OLUŞTURMA MERKEZİ (CLOS-131 `[114]`).
 *
 * ### ⚠ Oluşturma bitince PENCERE KAPANIYOR
 *
 * Kapanmadığında kullanıcı detay sayfasına yönlendiriliyor ama üstte duran pencerenin
 * saydam katmanı bütün tıklamaları yutuyordu: sözleşme oluşmuş, ekran açılmış, kullanıcı
 * hiçbir şey yapamıyor. Yönlendirme tek başına "iş bitti" demek değil.
 *
 * ### ⚠ Yöntem seçilmeden HİÇBİR KAYIT oluşmuyor
 *
 * Kart: *"yöntem seçimi öncesi BOŞ KAYIT OLUŞMAZ."* Merkez açıldığında tek yaptığı şey
 * seçenekleri göstermek; her yöntem kendi onayında yazıyor. "Önce bir taslak açalım,
 * kullanıcı doldurur" yaklaşımı, vazgeçen her kullanıcı için bir çöp kayıt bırakırdı.
 *
 * ### ⚠ İPTAL güvenli
 *
 * Pencere kapandığında yazılmış bir şey yok: dosya bile ancak GÖNDER'e basılınca
 * yükleniyor. Dosyayı seçildiği anda yükleseydik, vazgeçen kullanıcı arkasında sahipsiz
 * bir nesne bırakırdı.
 *
 * ### ⚠ İKİNCİ ADIM erişilebilirlik AĞACINA çıkıyor (CLOS-861)
 *
 * Yöntem seçilince adım içeriği aynı pencerede değişiyor, pencere KAPANIP AÇILMIYOR.
 * `Modal`'ın odak kapanı yalnız açılışta çalıştığı için, seçilen düğme sökülünce odak
 * `<body>`'ye düşüyordu — panel `aria-modal="true"` taşıdığından ekran okuyucu panelin
 * dışını ağaçtan siler; kullanıcının imleci silinmiş bölgede kalır ve ikinci adımı hiç
 * duymazdı. Ekranda duran forma göz ulaşıyor, klavye ve ekran okuyucu ulaşamıyordu.
 *
 * İki şey birlikte gerekiyor ve biri ötekinin yerini tutmuyor:
 * 1. Adım kapsayıcısı KENDİ ADIYLA ağaçta duruyor (`aria-label`'lı `<form>` → `form`
 *    rolü). Adsız bir `<form>` ağaçta hiçbir düğüm üretmez; "hangi adımdayım" sorusunun
 *    yanıtı yok olurdu — pencerenin başlığı üç adımda da "Yeni Sözleşme".
 * 2. Odak adımın İLK ALANINA taşınıyor. Odağı yalnız panele geri çekmek yetmezdi:
 *    kullanıcı Kapat düğmesine düşer, forma yeniden Tab'lamak zorunda kalırdı.
 *
 * ⚠ Düzeltme `Modal`'da DEĞİL sayfa akışında: kapanı içerik her değiştiğinde yeniden
 * çalıştırmak, kullanıcı yazarken odağı çalardı. Adımın hangi alanla başladığını yalnız
 * akışın kendisi bilir.
 *
 * ### ⚠ Aktif çalışma alanı GÖRÜNÜR
 *
 * Kartın kriteri. Birden çok firmada çalışan bir hukukçu, sözleşmeyi yanlış firmada
 * açmayı ancak sonradan fark ederdi ve geri alması kolay olmazdı.
 */

type Yontem = "empty" | "file" | "template";

/**
 * Adımın erişilebilir ADI. Pencere başlığı ("Yeni Sözleşme") adım değişince değişmiyor;
 * ağaçtaki tek ayırt edici işaret bu ad.
 */
const ADIM_ADLARI: Record<"empty" | "file", string> = {
  empty: "Boş sözleşme bilgileri",
  file: "Dosyadan sözleşme bilgileri",
};

/**
 * Yöntem satır kartı.
 *
 * ### ⚠ Erişilebilir ad YALNIZ BAŞLIK
 *
 * Açıklama düğmenin içinde duruyor ama `aria-labelledby` yalnız başlığı gösteriyor;
 * açıklama `aria-describedby` ile ayrı okunuyor. İkisi birden ada karışsaydı ekran
 * okuyucu kullanıcısı düğmeyi "Boş sözleşme Yeni bir sözleşmeye sıfırdan başlayın"
 * diye duyardı — ve üç testin aradığı kanonik ad ("Boş sözleşme", "Dosyadan",
 * "Şablondan") sessizce kaybolurdu.
 */
function YontemKarti({
  id,
  icon,
  baslik,
  aciklama,
  onClick,
  buttonRef,
}: {
  id: string;
  icon: ReactNode;
  baslik: string;
  aciklama: string;
  onClick: () => void;
  /** ⚠ Yalnız İLK yöntem taşıyor: geri dönüşte odağın ineceği yer (CLOS-876). */
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-labelledby={`yontem-${id}-baslik`}
      aria-describedby={`yontem-${id}-aciklama`}
      className="group flex min-h-[72px] w-full items-center gap-4 rounded-lg border border-border-default bg-surface px-4 py-3.5 text-left transition-colors hover:border-primary-muted hover:bg-primary-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <span
        aria-hidden="true"
        className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary [&_svg]:size-6"
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span
          id={`yontem-${id}-baslik`}
          className="block text-[15px] font-semibold text-fg-heading"
        >
          {baslik}
        </span>
        <span id={`yontem-${id}-aciklama`} className="mt-0.5 block text-[13px] text-fg-muted">
          {aciklama}
        </span>
      </span>
      <ChevronRightIcon className="size-5 shrink-0 text-fg-faint transition-colors group-hover:text-primary" />
    </button>
  );
}

export function NewContractCenter({ onClose }: { onClose: () => void }) {
  const { activeTenantId, activeTenant, can } = useAuth();
  const tenantId = activeTenantId!;
  const navigate = useNavigate();

  const [yontem, setYontem] = useState<Yontem | null>(null);
  const [baslik, setBaslik] = useState("");
  const [dosya, setDosya] = useState<File | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  /**
   * ⚠ Bağımlılık YALNIZ `yontem` — ADIM değişince taşı, adım İÇİNDEKİ durum değişimlerinde
   * taşıma. Liste genişletilirse (ya da dizi hiç verilmezse) zarar somut ve ölçüldü: dosya
   * adımında kullanıcı odağı dosya girdisine taşıyıp bir dosya seçtiğinde doğan render,
   * odağı başlık alanına GERİ ÇALAR — kullanıcı az önce doldurduğu alandan koparılır.
   * (Gerekçe "başlığa tekrar odaklanmak imleci sıçratır" DEĞİL: aynı alana `focus()`
   * çağırmak etkisizdir, bekçi de bu yanlış eksende yazılsaydı hiç kırmızı yanmazdı.)
   *
   * ### ⚠ TERS YÖN de taşıyor (CLOS-876) — ama YALNIZ GERİ DÖNÜLDÜĞÜNDE
   *
   * İki "Geri" düğmesi de sökülünce odak yine `<body>`'ye düşüyordu ve panel `aria-modal`
   * olduğu için imleç ağaçtan silinmiş bölgede kalıyordu: kusur ileri yöndekiyle aynı
   * sınıftan, yönü ters. Odak ilk yöntem düğmesine iniyor — panele ya da başlığa geri
   * çekmek yetmezdi, kullanıcı seçeneklere yeniden Tab'lamak zorunda kalırdı.
   *
   * ⚠ AÇILIŞTA odak ÇALINMIYOR: `onceki` ile bir adımdan DÖNÜLDÜĞÜ ayırt ediliyor ve bu
   * koşul YÜK TAŞIYOR — zararı açılışta değil KAPANIŞTA. `useFocusTrap` "açılmadan önceki
   * odak"ı kendi çalıştığı anda saklıyor ve ebeveyn effect olarak bu effect'ten SONRA
   * koşuyor. Koşul yalnız `yontem === null` olsaydı, açılışta odağı biz alırdık, kapan
   * panelin İÇİNDEKİ düğmeyi "önceki odak" sanırdı ve pencere kapanınca odağı sökülmüş bir
   * düğüme iade ederdi: kullanıcı yine `<body>`'de kalırdı. Kusur yok olmaz, girişten
   * çıkışa taşınırdı. (Açılışta odağın nereye düştüğünü ölçmek bunu GÖSTERMEZ: kapan
   * sonradan koşup odağı "Kapat"a aldığı için o eksende yazılan bekçi hiç kırmızı yanmadı.)
   */
  const baslikRef = useRef<HTMLInputElement>(null);
  const ilkYontemRef = useRef<HTMLButtonElement>(null);
  const oncekiYontem = useRef<Yontem | null>(null);
  useEffect(() => {
    const onceki = oncekiYontem.current;
    oncekiYontem.current = yontem;
    if (yontem === "empty" || yontem === "file") {
      baslikRef.current?.focus();
      return;
    }
    if (yontem === null && onceki !== null) ilkYontemRef.current?.focus();
  }, [yontem]);

  /**
   * ⚠ Kabul kuralları SUNUCUDAN. Ekrana sabitlemek, kartın *"parametreler hard-code
   * edilmez"* açık kararını delerdi ve konfigürasyon değiştiğinde iki yer güncellenirdi.
   */
  const politika = useQuery({
    enabled: yontem === "file",
    queryKey: tenantKey(tenantId, "contract-file-policy"),
    queryFn: async () =>
      (await api<ContractFilePolicy>("/api/contracts/file-policy", { tenantId })).data,
  });

  /**
   * ⚠ Seçim anında ÖN kontrol — bir güvenlik kararı DEĞİL, erken geri bildirim. Aynı
   * sorular sunucuda dosyanın gerçek baytlarıyla yeniden soruluyor. Ön kontrol olmadan
   * kullanıcı 25 MB'lık bir dosyayı depoya yükleyip reddi ancak yükleme bittikten sonra
   * öğreniyordu.
   */
  function dosyaSecildi(secilen: File | null) {
    setDosya(secilen);
    setHata(null);
    if (!secilen || !politika.data) return;
    const ret = precheckContractFile(politika.data, {
      contentType: secilen.type,
      sizeBytes: secilen.size,
    });
    if (ret) {
      setDosya(null);
      setHata(CONTRACT_FILE_REJECTION_NAMES[ret]);
    }
  }

  const sablonlar = useQuery({
    enabled: yontem === "template",
    queryKey: tenantKey(tenantId, "templates", "secim"),
    queryFn: async () =>
      (await api<ContractTemplateView[]>("/api/templates?limit=50", { tenantId })).data,
  });

  async function olustur() {
    setGonderiliyor(true);
    setHata(null);
    try {
      if (yontem === "empty") {
        const sonuc = await api<{ id: string }>("/api/contracts", {
          method: "POST",
          tenantId,
          body: JSON.stringify({ title: baslik.trim() }),
        });
        void navigate(`/app/sozlesmeler/${sonuc.data.id}`);
        onClose();
        return;
      }
      if (yontem === "file") {
        if (!dosya) {
          setHata("Sözleşme dosyası seçin");
          return;
        }
        /** ⚠ Dosya ANCAK burada yükleniyor: seçimde yükleseydik iptal çöp bırakırdı. */
        const yuklenen = await uploadFile({
          file: dosya,
          domain: "contract",
          entityId: crypto.randomUUID(),
          tenantId,
        });
        const sonuc = await api<ContractWithVersion>("/api/contracts/from-file", {
          method: "POST",
          tenantId,
          body: JSON.stringify({
            title: baslik.trim(),
            storageObjectId: yuklenen.storageObjectId,
          }),
        });
        void navigate(`/app/sozlesmeler/${sonuc.data.contract.id}`);
        onClose();
        return;
      }
    } catch (err) {
      /**
       * ⚠ ALAN MESAJI ZARF METNİNİN ÖNÜNDE (BUG-015 · Mega E2E'de ölçüldü).
       *
       * Sunucu şema hatasında zarfa GENEL metin ("Girilen bilgiler geçersiz") yazıp asıl
       * açıklamayı `fields[].message`'a koyuyor — izin verilmeyen dosya türünde
       * "Bu dosya türü desteklenmiyor". Burada yalnız `err.message` basıldığı için
       * kullanıcı reddin SEBEBİNİ hiç görmüyordu; bu ekranın alan gösteren bir formu yok,
       * yani mesajın gidecek başka yeri de yoktu.
       *
       * ⚠ Ortak motor kullanılıyor, ikinci bir eşleme yazılmadı: `resolveErrorState` aynı
       * önceliği uyguluyor ve güvensiz kodlarda alan ayrıntısını sızdırmıyor.
       */
      setHata(
        err instanceof ApiRequestError
          ? resolveErrorState(err, { idempotent: false }).description
          : "Sözleşme oluşturulamadı",
      );
    } finally {
      setGonderiliyor(false);
    }
  }

  if (yontem === null) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          Sözleşmenizi oluşturmak için aşağıdaki yöntemlerden size en uygun olanı seçin.
        </p>

        {/**
         * Yöntemler ALT ALTA: onaylı tasarımda her yöntem bir satır kartı — başlık,
         * açıklama ve sağda bir ok. Üç sütuna sıkıştırıldığında açıklamalar sığmıyordu ve
         * kullanıcı yalnız iki kelimeye bakarak seçim yapıyordu.
         */}
        <div className="flex flex-col gap-2.5">
          <YontemKarti
            id="empty"
            icon={<ContractIcon />}
            baslik="Boş sözleşme"
            aciklama="Yeni bir sözleşmeye sıfırdan başlayın."
            onClick={() => setYontem("empty")}
            buttonRef={ilkYontemRef}
          />
          <YontemKarti
            id="file"
            icon={<UploadIcon />}
            baslik="Dosyadan"
            aciklama="Mevcut bir sözleşme taslağınızı yükleyin, düzenlemeye Closer içinde devam edin."
            onClick={() => setYontem("file")}
          />
          {/*
            Şablon yöntemi İKİ yetki istiyor (CLOS-129): kütüphaneyi görmek ve şablonu
            KULLANMAK.

            ⚠ Yalnız `template.view` sorulduğunda, kütüphaneyi okuyabilen ama kullanma
            yetkisi olmayan kullanıcıya yöntem gösteriliyor, şablonu seçiyor ve ancak
            oluşturma anında 403 alıyordu. Sunucu kapısı doğruydu; ekran onu iki adım geç
            söylüyordu.
          */}
          {/*
            ⚠ AI yöntemi KENDİ SAYFASINA gidiyor (şablon yöntemiyle aynı desen): üretim
            uzun sürüyor, çıktı uzun ve kullanıcı metni incelerken pencerenin dar
            gövdesinde kalmamalı. Ayrı yetki YOK — sunucu kapısı `contract.create` ve bu
            merkez zaten o yetkiyle açılıyor; ikinci bir izin icat etmek kartsız olurdu.
          */}
          <YontemKarti
            id="ai"
            icon={<SparklesIcon />}
            baslik="AI ile taslak"
            aciklama="Amaç ve taraf bilgilerinden AI taslağı üretin; kabul etmeden sözleşme oluşmaz."
            onClick={() => {
              onClose();
              void navigate("/app/sozlesmeler/yeni/ai-taslak");
            }}
          />
          {can("template.view") && can("template.use") && (
            <YontemKarti
              id="template"
              icon={<TemplateIcon />}
              baslik="Şablondan"
              aciklama="Mevcut şablonlarınızı kullanarak hızlıca sözleşme oluşturun."
              onClick={() => setYontem("template")}
            />
          )}
        </div>

        {/* ⚠ Aktif çalışma alanı: yanlış firmada sözleşme açmak geri alması kolay bir hata değil. */}
        <InfoBanner>
          Yeni sözleşme, mevcut çalışma alanınız olan{" "}
          <span className="font-semibold text-fg-heading">{activeTenant?.name ?? "—"}</span> içinde
          oluşturulacaktır.
        </InfoBanner>
      </div>
    );
  }

  if (yontem === "template") {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-soft">Kullanmak istediğiniz şablonu seçin.</p>
        {sablonlar.isPending ? (
          <div role="status" aria-label="Şablonlar yükleniyor">
            <Skeleton lines={3} />
          </div>
        ) : sablonlar.error ? (
          /*
            ⚠ HATA "yok" DEĞİL (kusur avı, 26 Ağustos): şablon listesi getirilemediğinde
            ekran "Kullanılabilir şablon yok" diyordu. Kullanıcı kütüphanesinin boş
            olduğunu sanıp şablon aramaktan vazgeçiyordu; oysa liste hiç gelmemişti.
          */
          <StateView
            state={resolveErrorState(sablonlar.error, {
              idempotent: true,
              overrides: {
                error: {
                  title: "Şablonlar yüklenemedi",
                  description: "Liste şu an getirilemedi; tekrar deneyebilirsiniz.",
                },
              },
            })}
            onRetry={() => void sablonlar.refetch()}
          />
        ) : (sablonlar.data?.length ?? 0) === 0 ? (
          /*
            ⚠ BOŞ DURUM ÇIKMAZ SOKAK DEĞİL (canlı tur, 4 Eylül).

            Ekran "şablon yok" deyip orada bırakıyordu: kullanıcı ne yapacağını
            bilmiyordu, çünkü şablon kütüphanesi Sözleşmeler altında bir SEKME ve yeni
            kullanıcı orayı bilmiyor. Sıfırdan kurulan bir çalışma alanında bu durum
            İSTİSNA değil, ilk karşılaşılan hâl.

            ⚠ Yönlendirme yeni şablon FORMUNA değil kütüphaneye: kullanıcı belki de
            paylaşılmış bir şablon bekliyordur, belki kendi yazacaktır. Kütüphane ikisini
            de gösteriyor ve kendi "Yeni şablon" düğmesini zaten taşıyor.
          */
          <EmptyState
            icon={<TemplateIcon />}
            title="Kullanılabilir şablon yok"
            description="Kütüphanede size açık, kullanımda bir şablon bulunmuyor."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  onClose();
                  navigate("/app/sablonlar");
                }}
              >
                Şablon kütüphanesine git
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-default">
            {(sablonlar.data ?? []).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-sm font-medium text-fg-heading transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-primary-soft focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
                  onClick={() => {
                    onClose();
                    void navigate(`/app/sozlesmeler/yeni/sablondan/${s.id}`);
                  }}
                >
                  <span className="min-w-0 truncate">{s.name}</span>
                  <span aria-hidden="true" className="shrink-0 text-sm text-primary">
                    Seç
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div>
          <Button type="button" variant="secondary" size="sm" onClick={() => setYontem(null)}>
            Geri
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      noValidate
      /** ⚠ Ad OLMADAN `<form>` ağaçta hiç görünmez; rolü açan şey adın kendisi. */
      aria-label={ADIM_ADLARI[yontem]}
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void olustur();
      }}
    >
      <FormField label="Sözleşme başlığı" required>
        <Input
          ref={baslikRef}
          value={baslik}
          onChange={(e) => setBaslik(e.target.value)}
          placeholder="Örn. Müvekkil A — Hizmet Sözleşmesi"
        />
      </FormField>

      {yontem === "file" && (
        <FormField label="Sözleşme dosyası">
          {/**
           * Dosya girdisi kendi kutusunda: tarayıcının çıplak `<input type=file>`'ı her
           * platformda başka görünüyor ve formun geri kalanının yanında biçimsiz kalıyor.
           * Kap yalnız çerçeve — girdinin kendisi olduğu gibi duruyor, gizlenip yerine
           * sahte bir düğme konsaydı klavye ve sürükle-bırak davranışı kaybolurdu.
           */}
          <div className="rounded-lg border border-dashed border-border-strong bg-canvas px-4 py-5">
            <FileInput
              aria-label="Sözleşme dosyası seç"
              accept={(politika.data?.allowedContentTypes ?? []).join(",")}
              onChange={(e) => dosyaSecildi(e.target.files?.[0] ?? null)}
            />
          </div>
        </FormField>
      )}

      {hata && (
        <p role="alert" className="text-sm text-danger">
          {hata}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={() => setYontem(null)}>
          Geri
        </Button>
        <Button type="submit" loading={gonderiliyor} disabled={baslik.trim().length === 0}>
          Oluştur
        </Button>
      </div>
    </form>
  );
}
